import {
  Component,
  OnInit,
  Input,
  ComponentFactoryResolver,
  ElementRef,
  AfterViewInit,
  ViewChild,
  HostListener,
  ChangeDetectorRef
} from '@angular/core';
import { DataEntityInputComponent } from '../../data-entity-input/data-entity-input.component';
import { SketchDataEntity } from 'src/app/models/data-entities/sketch-data-entity';
import { UntypedFormBuilder, NgForm, Validators } from '@angular/forms';
import { SketchFieldComponent } from '../sketch-field/sketch-field.component';
import SignaturePad, { PointGroup } from 'signature_pad';
import { isArray } from 'jquery';
import { WorkflowContextService } from 'src/app/services';
import { LayoutService } from 'src/app/services/layout.service';

@Component({
    selector: 'wm-sketch-data-entity-input',
    templateUrl: './sketch-data-entity-input.component.html',
    styleUrls: ['./sketch-data-entity-input.component.css'],
    standalone: false
})
export class SketchDataEntityInputComponent extends DataEntityInputComponent
  implements OnInit, AfterViewInit {
  @Input() entity: SketchDataEntity;
  @ViewChild(SketchFieldComponent, { static: false })
  public sketch: SketchFieldComponent;
  @ViewChild('sketchContainer1') public sketchContainer1: ElementRef;

  hasPath: boolean = false;
  imagePath: string = '';

  constructor(
    public layoutService: LayoutService,
    componentFactoryResolver: ComponentFactoryResolver,
    ref: ChangeDetectorRef,
    private _fb: UntypedFormBuilder,
    context : WorkflowContextService  
  ) {
    super(layoutService, componentFactoryResolver, ref, context);
  }

  onFormSubmit(form: NgForm) { }

  ngOnInit() {
    this.form.addControl(
      'sketchField1',
      this._fb.control(false, [Validators.nullValidator])
    );
  }

  cancelCalculation() {
    if (!this.hasPath) {
      setTimeout(() => {
        this.beResponsive();
        this.sketch.signaturePad.fromData(this.sketch.signaturePad.toData());
      }, 100);
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    if (!this.hasPath) {
      this.beResponsive();
      this.sketch.signaturePad.fromData(this.sketch.signaturePad.toData());
    }
  }

  // Mirror of SignatureDataEntityInputComponent.parseEntityValue — when the persisted
  // value carries only a storage `path` (e.g. an inspection-app sketch uploaded via the
  // document endpoint), we render a preview `<img>` in the template instead of the
  // signature-pad canvas. Kept in sync with the InkData envelope
  // ({ data?, image?, path? }) that the server accepts.
  private parseEntityValue() {
    let parsedValue = null;
    try {
      if (typeof this.entity.value === 'string') {
        parsedValue = JSON.parse(this.entity.value);
      } else {
        parsedValue = this.entity.value;
      }
    } catch {
      // do nothing
      console.log('Entity Value: ', this.entity.value);
    }

    if (parsedValue && parsedValue.path) {
      this.hasPath = true;
      this.imagePath = parsedValue.path;
    } else {
      this.hasPath = false;
      this.imagePath = '';
    }
  }

  public ngAfterViewInit() {
    this.parseEntityValue();

    if (!this.hasPath) {
      this.setOptions();
    }

    // Let parent resize before sizing the canvas, especially in modal dialogs.
    setTimeout(() => {
      if (!this.hasPath) {
        this.beResponsive();
      }

      if ((this.entity.value || '') !== '' && !this.hasPath) {
        let value: { data: PointGroup[]; image: string; path?: string };
        let parsedValue = null;

        try {
          parsedValue = JSON.parse(this.entity.value as string);
        }
        catch {
          console.log('Entity Value: ', { ...this.entity.value });
        }

        if (parsedValue) {
          if ((parsedValue.data !== null) && (parsedValue.image !== '')) {
            if (parsedValue.data) {
              if (parsedValue.data.indexOf('lx') > -1) {
                parsedValue.data = parsedValue.data
                  .replaceAll('lx', 'x')
                  .replaceAll('ly', 'y');
                parsedValue.data = `[` + parsedValue.data + `]`;
              } else {
                parsedValue = JSON.parse(this.entity.value as string);
              }

              value = parsedValue;
            }

            if (typeof parsedValue.data === 'string') {
              parsedValue.data = JSON.parse(parsedValue.data as string);
            }
          } else {
            value = this.entity.value;
          }

          let newData = [];

          if (isArray(value.data)) {
            if (value.data.length > 0) {
              if (isArray(value.data[0])) {
                for (let pgIdx = 0; pgIdx < value.data.length; pgIdx++) {
                  newData.push({
                    penColor: 'rgb(0, 0, 0)',
                    dotSize: 0,
                    minWidth: 0.5,
                    maxWidth: 2.5,
                    velocityFilterWeight: 0.7,
                    points: value.data[pgIdx]
                  });
                }
              } else {
                newData = value.data;
              }
            }
          }

          if (value && value.data && !this.hasPath) {
            this.sketch.signaturePad.fromData(newData);
          }
          this.form.controls[this.entity.templateCode].setValue(
            this.entity.value
          );
        }
      }
    }, 100);
  }

  // Set the dimensions of the sketch pad canvas — maintain 1:1 aspect ratio
  public beResponsive() {
    this.size(this.sketchContainer1, this.sketch);
  }

  public size(container: ElementRef, sketch: SketchFieldComponent) {
    const side = container.nativeElement.clientWidth;
    sketch.signaturePad.set('canvasWidth', side);
    sketch.signaturePad.set('canvasHeight', side);
  }

  public setOptions() {
    this.sketch.signaturePad.set('penColor', 'rgb(0, 0, 0)');
    this.sketch.signaturePad.set('backgroundColor', 'rgba(0, 0, 0, 0)');
  }

  public clear() {
    this.sketch.clear();
    this.entity.value = null;
    this.form.controls[this.entity.templateCode].setValue(null);
  }

  public finishedSketching(data: SignaturePad) {
    this.entity.value = {
      image: data.toDataURL('image/png', 1),
      data: data.toData()
    };
    this.form.controls[this.entity.templateCode].setValue(this.entity.value);
  }
}
